import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  createFilterOptions,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined,
  ArrowDownwardOutlined,
  CandlestickChartOutlined,
  GroupsOutlined,
  HubOutlined,
  TrendingUpOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { formatCurrency, formatCurrencyCompact } from '../../../utils/CurrencyFormat';
import { getControlCentreOverview } from '../../../services/request/CapitalAnalytics';
import type { ControlCentreOverview, PartnerControlSnapshot } from '../../../models/CapitalAnalyticsTypes';

const fmt = (n: number) => formatCurrency(n, 'INR', true);
const fmtCompact = (n: number) => formatCurrencyCompact(n, 'INR', true);

const filterPartners = createFilterOptions<PartnerControlSnapshot>({
  stringify: (p) => `${p.partnerName} ${fmtCompact(p.totalContributed)}`,
});

const partnerOptionLabel = (p: PartnerControlSnapshot | null) =>
  p ? `${p.partnerName} — ${fmtCompact(p.totalContributed)}` : '';

const QUICK_LINKS = [
  { label: 'Capital Partners', to: '/admin/capitals/partners', icon: <GroupsOutlined /> },
  { label: 'Stock Picks', to: '/admin/capitals/stock-picks', icon: <CandlestickChartOutlined /> },
  { label: 'Margin report', to: '/admin/capitals/analytics', icon: <TrendingUpOutlined /> },
  { label: 'Maturity warnings', to: '/admin/capitals/analytics', icon: <WarningAmberOutlined /> },
];

const FlowNode: React.FC<{
  title: string;
  subtitle?: string;
  amount?: number;
  highlight?: boolean;
  color?: string;
  children?: React.ReactNode;
}> = ({ title, subtitle, amount, highlight, color = '#1565C0', children }) => (
  <Card
    elevation={highlight ? 4 : 1}
    sx={{
      borderRadius: 2,
      border: highlight ? `2px solid ${color}` : '1px solid',
      borderColor: highlight ? color : 'divider',
      bgcolor: highlight ? alpha(color, 0.06) : 'background.paper',
      minWidth: { xs: '100%', sm: 220 },
      flex: 1,
    }}
  >
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10, letterSpacing: '0.14em' }}>
        {title}
      </Typography>
      {amount !== undefined && (
        <Typography variant="h5" sx={{ fontWeight: 800, color, mt: 0.5 }}>
          {fmtCompact(amount)}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const PartnerFlowDiagram: React.FC<{ partner: PartnerControlSnapshot }> = ({ partner }) => (
  <Box sx={{ py: 2 }}>
    <Stack alignItems="center" spacing={0}>
      <FlowNode
        title="Control Centre"
        subtitle={`Total amount by ${partner.partnerName}`}
        amount={partner.totalContributed}
        highlight
        color="#1a237e"
      />
      <ArrowDownwardOutlined sx={{ color: 'text.disabled', my: 1 }} />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ width: '100%', alignItems: 'stretch' }}
      >
        <FlowNode
          title="Stock capitals"
          subtitle={`${partner.partnerName} invested in stocks`}
          amount={partner.stockCapitalsInvested}
          color="#2E7D32"
        >
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {partner.activeStockPositions} open position{partner.activeStockPositions === 1 ? '' : 's'}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
              Unrealised P&amp;L {fmtCompact(partner.stockUnrealizedPnl)}
              {partner.stockRealizedPnl > 0 && ` · Realised +${fmtCompact(partner.stockRealizedPnl)}`}
            </Typography>
          </Stack>
        </FlowNode>

        <FlowNode
          title="Lending"
          subtitle={`${partner.partnerName} invested in lending`}
          amount={partner.lendingInvested}
          color="#E65100"
        >
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              <Chip size="small" label={`Borrower pays ${partner.borrowerLendingRatePctMonthly}%/mo`} />
              <Chip size="small" color="success" variant="outlined" label={`Partner ${partner.partnerLendingRatePctMonthly}%`} />
              <Chip size="small" color="primary" variant="outlined" label={`You ${partner.operatorMarginPctMonthly}%`} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              ≈ {fmt(partner.monthlyPartnerLendingIncome)}/mo to partner · {fmt(partner.monthlyOperatorLendingMargin)}/mo your margin
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {partner.activeLendingDebits} active debit{partner.activeLendingDebits === 1 ? '' : 's'}
            </Typography>
          </Stack>
        </FlowNode>
      </Stack>
      {partner.idleCapital > 0 && (
        <>
          <ArrowDownwardOutlined sx={{ color: 'text.disabled', my: 1 }} />
          <FlowNode
            title="Undeployed"
            subtitle="Awaiting stock pick or lending allocation"
            amount={partner.idleCapital}
            color="#546E7A"
          />
        </>
      )}
    </Stack>
  </Box>
);

const ControlCentreDashboard: React.FC = () => {
  const [overview, setOverview] = useState<ControlCentreOverview | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');

  useEffect(() => {
    getControlCentreOverview().then((data) => {
      setOverview(data);
      setSelectedId(data.partners[0]?.partnerId ?? '');
    });
  }, []);

  const selected = useMemo(
    () => overview?.partners.find((p) => p.partnerId === selectedId) ?? null,
    [overview, selectedId],
  );

  const sortedPartners = useMemo(
    () => [...(overview?.partners ?? [])].sort((a, b) =>
      a.partnerName.localeCompare(b.partnerName, undefined, { sensitivity: 'base' }),
    ),
    [overview?.partners],
  );

  const stockPct = overview && overview.totalCapitalContributed > 0
    ? (overview.totalStockCapitals / overview.totalCapitalContributed) * 100
    : 0;
  const lendPct = overview && overview.totalCapitalContributed > 0
    ? (overview.totalLendingDeployed / overview.totalCapitalContributed) * 100
    : 0;

  return (
    <Box sx={{ pb: 4 }}>
      {/* Hero */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0d1b4b 0%, #1a237e 45%, #283593 100%)',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: alpha('#fff', 0.06),
          }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <HubOutlined sx={{ fontSize: 28 }} />
          <Typography variant="overline" sx={{ letterSpacing: '0.2em', opacity: 0.85 }}>
            Capitals
          </Typography>
        </Stack>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
          Control Centre
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 640 }}>
          One place to oversee partner money — how much came in, what went to stock capitals vs lending,
          and the spread you keep (2% from borrower → 1.5% to partner → 0.5% for you).
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2.5 }}>
          {QUICK_LINKS.map((link) => (
            <Button
              key={link.label}
              component={RouterLink}
              to={link.to}
              size="small"
              variant="outlined"
              startIcon={link.icon}
              sx={{
                color: '#fff',
                borderColor: alpha('#fff', 0.4),
                '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.1) },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Stack>
      </Box>

      {!overview ? (
        <LinearProgress />
      ) : (
        <>
          {/* Global KPIs */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: 4, borderLeftColor: '#1a237e' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Total partner capital</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{fmtCompact(overview.totalCapitalContributed)}</Typography>
                  <Typography variant="caption" color="text.secondary">{overview.totalPartners} partners</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: 4, borderLeftColor: '#2E7D32' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Stock capitals</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{fmtCompact(overview.totalStockCapitals)}</Typography>
                  <Typography variant="caption" color="text.secondary">{stockPct.toFixed(0)}% of total</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: 4, borderLeftColor: '#E65100' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Lending deployed</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{fmtCompact(overview.totalLendingDeployed)}</Typography>
                  <Typography variant="caption" color="text.secondary">{lendPct.toFixed(0)}% of total</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', borderLeft: 4, borderLeftColor: '#546E7A' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Your monthly margin</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{fmtCompact(overview.totalOperatorMarginMonthly)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtCompact(overview.totalPartnerLendingIncomeMonthly)}/mo paid to partners
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Partner flow */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                spacing={2}
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography variant="overline" color="text.secondary">Partner view</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Capital flow diagram</Typography>
                </Box>
                <Autocomplete
                  size="small"
                  options={sortedPartners}
                  value={selected}
                  onChange={(_, partner) => setSelectedId(partner?.partnerId ?? '')}
                  getOptionLabel={partnerOptionLabel}
                  isOptionEqualToValue={(a, b) => a.partnerId === b.partnerId}
                  filterOptions={filterPartners}
                  disableClearable={sortedPartners.length > 0}
                  noOptionsText="No matching partners"
                  ListboxProps={{ style: { maxHeight: 320 } }}
                  sx={{ minWidth: { xs: '100%', sm: 300 }, maxWidth: 420 }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.partnerId}>
                      <Stack direction="row" justifyContent="space-between" width="100%" spacing={2}>
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {option.partnerName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
                          {fmtCompact(option.totalContributed)}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select partner"
                      placeholder="Search by name…"
                    />
                  )}
                />
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {selected ? (
                <PartnerFlowDiagram partner={selected} />
              ) : (
                <Typography color="text.secondary">Select a partner to view their flow.</Typography>
              )}
            </CardContent>
          </Card>

          {/* All partners table */}
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Overview</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>All partners at a glance</Typography>
                </Box>
                {overview.criticalMaturities > 0 && (
                  <Chip
                    icon={<WarningAmberOutlined />}
                    label={`${overview.criticalMaturities} critical maturit${overview.criticalMaturities === 1 ? 'y' : 'ies'}`}
                    color="error"
                    variant="outlined"
                    component={RouterLink}
                    to="/admin/capitals/analytics"
                    clickable
                  />
                )}
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Partner</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Stock capitals</TableCell>
                      <TableCell align="right">Lending</TableCell>
                      <TableCell align="right">Idle</TableCell>
                      <TableCell align="right">Partner rate</TableCell>
                      <TableCell align="right">Your margin/mo</TableCell>
                      <TableCell align="right">Next maturity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overview.partners.map((p) => (
                      <TableRow
                        key={p.partnerId}
                        hover
                        selected={p.partnerId === selectedId}
                        onClick={() => setSelectedId(p.partnerId)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AccountBalanceWalletOutlined fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.partnerName}</Typography>
                            {p.status === 'WATCH' && (
                              <Chip label="Watch" size="small" color="warning" variant="outlined" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCompact(p.totalContributed)}</TableCell>
                        <TableCell align="right">{fmtCompact(p.stockCapitalsInvested)}</TableCell>
                        <TableCell align="right">{fmtCompact(p.lendingInvested)}</TableCell>
                        <TableCell align="right">{fmtCompact(p.idleCapital)}</TableCell>
                        <TableCell align="right">{p.partnerLendingRatePctMonthly}%/mo</TableCell>
                        <TableCell align="right">{fmtCompact(p.monthlyOperatorLendingMargin)}</TableCell>
                        <TableCell align="right">
                          {p.nextMaturityDays != null ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: p.nextMaturityDays <= 14 ? 'error.main' : p.nextMaturityDays <= 45 ? 'warning.main' : 'text.primary',
                              }}
                            >
                              {p.nextMaturityDays}d
                            </Typography>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ControlCentreDashboard;
